import type {
  BackendSegmentationAgent,
  BatchResult,
  SegmentationJob,
  SegmentationProgress,
  SegmentationResult,
  SegmentationSettings,
  WorkerMessage,
} from '../components/segmentation/segmentation-typing';
import type {
  DicomPullRequest,
  ImageRetrivalInfo,
  MetaData,
} from '@/src/components/backend/payload-typing';

import { computed, ref } from 'vue';

import {
  // getAllAsImages,
  getAllImageUIDs,
  getPayloadFromImageUIDs,
} from '@/src/components/backend/payload-tools';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { getPolygonAreaArray } from '@/src/store/contours/contour-processing/getPolygonArea';
import { Orientation } from '@/src/store/dicom-web/classification-typing';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { seriesClassification } from '@/src/store/dicom-web/SeriesClassification';

import { useContourContainerStore } from '../store/contours/contour-container-store';
import {
  ContourLabel,
  encodeContourLabelItemId,
} from '../store/contours/contour-definitions';
import { convertArrayToPointArray } from '../store/contours/contour-processing/convertArrayToPointArray';
import { useSegmentationSettingsStore } from '../store/contours/contour-segmentation';

import { useAlert } from './useAlert';

// Maximum number of images to process in a single batch
const MAX_IMAGES_PER_LOAD = 160;

/*
  !!NOTE THAT CURRENTLY THE CODE HERE ONLY CAN ADD THE CONTOURS TO THE STORE WHEN THEY ARE IN POLYLINE FORMAT!!
  !!HANDLING FOR THE OTHER FORMATS NEEDS TO BE IMPLEMENTED!!

  all results will contain:
  {
      "imageUID": sop_id,
      results
  }
  where results will depend on the returnAs parameter:

  case: 'polyline' - returns the coordinates of the contour for each type of contour
  most efficient if we want to handle contour extraction in the backend
  "coordinates": {
    outer_ring: [[x1, y1], [x2, y2], ...],
    inner_ring: [[x1, y1], [x2, y2], ...],
    rv_ring: [[x1, y1], [x2, y2], ...],
    insertion_point: [x, y]
  }
  note that this is true only for SAX image types. LAX or 4CH images will only contain outer_ring (being the myocardium)

  case: 'mask' - returns the whole mask as list
  not efficient but could be used for debug
  "mask": [
    [0, 0, 0, 0, ...],
    [0, 1, 1, 1, ...],
    ...
  ]

  case: 'mask_coordinates' - returns the coordinates of the mask along with mask label (except background 0)
  mask label depends on image type. SAX will be 0:bg, 1:lv, 2:myo, 3:rv
  LAX, 4CH, 3CH will be 0:bg, 1:myo
  most efficient if we want to handle contour extraction in the frontend
  "mask_coordinates": {
    "values": [ 1, 2, ...],
    "coords": [
      [x1, y1],
      [x2, y2],
      ...
    ]
  }
*/

// Add a flag to track if segmentation is active
const isSegmentationActive = ref(false);

// Reference to the active worker so it can be terminated on abort
let activeWorker: Worker | null = null;

// Batch processing queue
interface QueueItem {
  batchIndex: number;
  totalBatches: number;
  payload: DicomPullRequest[];
  metaData: MetaData;
  settings: SegmentationSettings;
}

export function useBackendSegmentation(): BackendSegmentationAgent {
  // Stores
  const contourContainerStore = useContourContainerStore();
  const segmentationSettingsStore = useSegmentationSettingsStore();

  // Alert system
  const alert = useAlert();
  // State management
  const resultsFromBackend = ref<BatchResult[]>([]);

  // Queue management
  const processingQueue = ref<QueueItem[]>([]);
  const isQueueProcessing = ref<boolean>(false);

  // Progress tracking
  const isProcessing = ref<boolean>(false);
  const progress = ref<number>(0);
  const totalImages = ref<number>(0);
  const processedCount = ref<number>(0);
  const currentBatch = ref<number>(0);
  const totalBatches = ref<number>(0);
  const segmentationMessageLog = ref<string[]>([]);

  // Computed progress state to expose
  const progressState = computed<SegmentationProgress>(() => ({
    isProcessing: isProcessing.value,
    progress: progress.value,
    totalImages: totalImages.value,
    processedCount: processedCount.value,
    messages: segmentationMessageLog.value,
    currentBatch: currentBatch.value,
    totalBatches: totalBatches.value,
  }));

  /**
   * Add contours to the contour container store
   */
  function addContourBatch(
    batch: BatchResult[],
    include?: string[],
    exclude?: string[],
  ) {
    const finalBatch = [];

    if (!batch || batch.length === 0) return;

    const id = encodeContourLabelItemId([0]);

    for (let i = 0; i < batch.length; i++) {
      const { contours, imageUID } = batch[i];

      // Check if the imageUID is included or excluded
      if (include && !include.includes(imageUID)) continue;
      if (exclude && exclude.includes(imageUID)) continue;
      if (!contours || contours.length === 0) continue;
      // Process each contour in the batch

      for (const contour of contours) {
        const { label, points } = contour;
        if (!label || !points || points.length === 0) continue;
        // Check if Label is valid
        if (!Object.values(ContourLabel).includes(label)) {
          alert.warning(`Invalid contour label: ${label}`);
          continue;
        }
        const pointsArray = convertArrayToPointArray(points);
        finalBatch.push({
          UID: imageUID,
          points: pointsArray,
          label,
          id,
        });
      }
    }
    contourContainerStore.addContoursBatch(finalBatch);
  }

  /**
   * Clear the current processing queue
   */
  function clearQueue() {
    processingQueue.value = [];
    isQueueProcessing.value = false;
    currentBatch.value = 0;
    totalBatches.value = 0;
  }

  function updateAutoModelSelection(
    settings: SegmentationSettings,
    scannerSystem: string | undefined,
  ) {
    if (settings.SAX_MODEL_NAME !== 'auto') return settings;

    const localSettings = { ...settings };

    if (scannerSystem && /siemens/i.test(scannerSystem)) {
      localSettings.SAX_MODEL_NAME = 'cinema';
      return localSettings;
    }

    if (scannerSystem && /philips/i.test(scannerSystem)) {
      localSettings.SAX_MODEL_NAME = 'corseg';
      return localSettings;
    }

    localSettings.SAX_MODEL_NAME = 'nnunet';
    return localSettings;
  }

  async function buildQueueItemsFromJobs(
    jobs: SegmentationJob[],
    settings: SegmentationSettings,
    isFromFunctionModule = false,
  ): Promise<{ queueItems: QueueItem[]; totalPayloadImages: number }> {
    const queueItems: QueueItem[] = [];
    let totalPayloadImages = 0;

    for (let j = 0; j < jobs.length; j++) {
      const job = jobs[j];
      const imageUIDs = [...new Set(job.imageUIDs.filter(Boolean))];

      if (imageUIDs.length === 0) {
        continue;
      }

      const { dicomPullRequestObject, metaData }
        = await getPayloadFromImageUIDs(imageUIDs);

      if (!dicomPullRequestObject || dicomPullRequestObject.length === 0) {
        continue;
      }

      // Force a single orientation for each job so every produced batch stays orientation-pure.
      const seriesUIDsInJob = new Set(
        dicomPullRequestObject.map(req => req.SeriesInstanceUID),
      );
      seriesUIDsInJob.forEach((seriesUID) => {
        metaData.classificationBySeries[seriesUID] = job.orientation;
      });

      const processedDicomRequest = dicomPullRequestObject;
      // if (settings.SEND_IMAGES_DIRECTLY) {
      //   processedDicomRequest = await getAllAsImages(dicomPullRequestObject);
      //   metaData.imagesAreAttached = true;
      //   metaData.imagesAreCompressed = settings.IMAGES_COMPRESSED;
      //   metaData.imagesAreEncrypted = settings.ENCRYPT_IMAGES;
      // }

      metaData.postProcessing = settings.POST_PROCESSING;
      if (isFromFunctionModule) {
        metaData.batchImagesTogether = true;
      }

      totalPayloadImages += processedDicomRequest.length;

      for (
        let i = 0;
        i < processedDicomRequest.length;
        i += MAX_IMAGES_PER_LOAD
      ) {
        queueItems.push({
          batchIndex: 0,
          totalBatches: 0,
          payload: processedDicomRequest.slice(i, i + MAX_IMAGES_PER_LOAD),
          metaData: { ...metaData },
          settings,
        });
      }
    }

    return { queueItems, totalPayloadImages };
  }

  /**
   * Set up and initialize web worker for a single batch
   */
  async function setupWorker(
    dicomPullRequestObject: DicomPullRequest[],
    metaData: MetaData,
    settings: SegmentationSettings,
  ): Promise<BatchResult[]> {
    return new Promise((resolve, reject) => {
      isProcessing.value = true;
      progress.value = 0;

      const worker = new Worker(
        new URL(
          '../components/segmentation/backend-worker.ts',
          import.meta.url,
        ),
        {
          type: 'module',
        },
      );

      // Store reference for abort capability
      activeWorker = worker;

      worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
        const { type, progress: currentProgress, data, error } = {
          progress: undefined,
          data: undefined,
          error: undefined,
          ...e.data,
        };
        const batch = data;

        switch (type) {
          case 'PROCESS_COMPLETE': {
            // Calculate overall progress combining batch progress
            const batchProgressMsg
              = currentBatch.value > 0
                ? `Batch ${currentBatch.value}/${totalBatches.value} complete. `
                : '';

            segmentationMessageLog.value = [
              `${batchProgressMsg}Processing complete for current batch...`,
            ];

            alert.info(segmentationMessageLog.value.join('\n'));

            // Check if there are more batches to process
            if (processingQueue.value.length > 0) {
              worker.terminate();

              processNextBatch().then(resolve).catch(reject);
            }
            else {
              segmentationMessageLog.value = [
                'All batches complete. Applying results...',
              ];
              alert.success(segmentationMessageLog.value.join('\n'));
              isProcessing.value = false;
              isQueueProcessing.value = false;
              worker.terminate();
              resolve(resultsFromBackend.value);
            }
            break;
          }

          case 'SEGMENTATION_RESULT': {
            if (batch) {
              resultsFromBackend.value.push(batch);
              processedCount.value += 1;

              // Calculate progress within the current batch
              const batchProgress = currentProgress;
              progress.value = batchProgress;

              // Create progress message with batch information
              const batchInfo
                = totalBatches.value > 1
                  ? `Batch ${currentBatch.value}/${totalBatches.value}: `
                  : '';

              const progressMsg = `${batchInfo}Segmentation progress: ${progress.value.toFixed(
                2,
              )}%`;
              segmentationMessageLog.value = [progressMsg];

              alert.info(segmentationMessageLog.value.join('\n'));
            }
            break;
          }

          case 'DICOM_LOAD_DONE': {
            segmentationMessageLog.value.push('DICOM loading complete.');
            alert.info(segmentationMessageLog.value.join('\n'));
            break;
          }

          case 'ERROR': {
            segmentationMessageLog.value.push(`Error: ${error}`);
            alert.error(segmentationMessageLog.value.join('\n'));
            isProcessing.value = false;
            isQueueProcessing.value = false;
            worker.terminate();
            reject(new Error(error));
            break;
          }

          default:
            break;
        }
      };

      worker.postMessage({
        payload: dicomPullRequestObject,
        metaData,
        settings,
      });
    });
  }

  /**
   * Process the next batch in the queue
   */
  async function processNextBatch(): Promise<BatchResult[]> {
    if (processingQueue.value.length === 0) {
      isQueueProcessing.value = false;
      return Promise.resolve(resultsFromBackend.value);
    }

    isQueueProcessing.value = true;
    const nextBatch = processingQueue.value.shift()!;
    // tv: is this really necessary? If for cloning, use structuredClone()?
    const cleanedBatch = JSON.parse(JSON.stringify(nextBatch)) as typeof nextBatch;
    currentBatch.value = cleanedBatch.batchIndex;
    totalBatches.value = cleanedBatch.totalBatches;

    return setupWorker(
      cleanedBatch.payload,
      cleanedBatch.metaData,
      cleanedBatch.settings,
    );
  }

  /**
   * Abort the current segmentation process
   */
  function abort() {
    if (!isSegmentationActive.value) return;

    // Terminate the active worker
    if (activeWorker) {
      activeWorker.terminate();
      activeWorker = null;
    }

    // Clear processing state
    clearQueue();
    resultsFromBackend.value = [];
    isProcessing.value = false;
    isQueueProcessing.value = false;
    isSegmentationActive.value = false;

    // Notify user
    alert.warning('Segmentation aborted.');
    segmentationMessageLog.value = ['Segmentation aborted by user.'];
  }

  async function segmentJobs(
    jobs: SegmentationJob[],
    isFromFunctionModule = false,
    addToStore = false,
  ): Promise<SegmentationResult> {
    if (isSegmentationActive.value) {
      abort();
      return { results: [] };
    }

    const validJobs = jobs.filter(job => job.imageUIDs.length > 0);
    if (validJobs.length === 0) {
      alert.warning('No images found for segmentation.');
      return { results: [] };
    }

    const undefinedOrientationJobs = validJobs.filter(
      job => job.orientation === Orientation.Undefined,
    );
    if (undefinedOrientationJobs.length > 0) {
      alert.error(
        'One or more segmentation jobs have undefined orientation. Please classify the slice orientation first.',
      );
      return { results: [] };
    }

    const currentStudyStore = useCurrentStudyStore();
    isSegmentationActive.value = true;
    clearQueue();
    resultsFromBackend.value = [];

    let settings = segmentationSettingsStore.getSettings();
    const studyInfo = currentStudyStore.getStudyInfo;
    const scannerSystem = studyInfo.Manufacturer;
    settings = updateAutoModelSelection(settings, scannerSystem);

    segmentationMessageLog.value = ['Starting segmentation process...'];
    alert.info(segmentationMessageLog.value.join('\n'));

    try {
      const { queueItems, totalPayloadImages } = await buildQueueItemsFromJobs(
        validJobs,
        settings,
        isFromFunctionModule,
      );

      if (queueItems.length === 0 || totalPayloadImages === 0) {
        alert.warning('No valid segmentation payload could be built.');
        return { results: [] };
      }

      totalImages.value = totalPayloadImages;
      processedCount.value = 0;

      totalBatches.value = queueItems.length;
      currentBatch.value = 0;

      queueItems.forEach((item, idx) => {
        processingQueue.value.push({
          ...item,
          batchIndex: idx + 1,
          totalBatches: queueItems.length,
        });
      });

      segmentationMessageLog.value.push(
        `Created ${queueItems.length} batches for processing. Using model: ${settings.SAX_MODEL_NAME}.`,
      );
      alert.info(segmentationMessageLog.value.join('\n'));

      const results = await processNextBatch();
      if (addToStore) {
        addContourBatch(results);
      }
      return { results };
    }
    catch (error) {
      const errorMessage
        = error instanceof Error ? error.message : 'Unknown error occurred';
      segmentationMessageLog.value.push(`Error: ${errorMessage}`);
      alert.error(segmentationMessageLog.value.join('\n'));
      throw error;
    }
    finally {
      isSegmentationActive.value = false;
    }
  }

  async function segmentActiveViewers(
    viewerIDs: string[],
  ): Promise<SegmentationResult> {
    const currentStudyStore = useCurrentStudyStore();
    const viewportActions = useViewportActions();

    const jobs: SegmentationJob[] = [];

    viewerIDs.forEach((viewerID) => {
      const imageUIDs
        = currentStudyStore.getSliceImageUIDsbyView(viewerID) ?? [];
      const studyObj = viewportActions.getStudyObjectByViewerId(viewerID);
      const excludedImages = studyObj?.getExcludedImages() ?? new Set<string>();
      const includedUIDs = imageUIDs.filter(uid => !excludedImages.has(uid));

      const orientation
        = seriesClassification.getClassificationByViewport(viewerID)
          ?.orientation ?? Orientation.Undefined;

      jobs.push({
        viewerID,
        imageUIDs: includedUIDs,
        orientation,
      });
    });

    const segmentationResult = await segmentJobs(jobs);
    addContourBatch(segmentationResult.results);
    return segmentationResult;
  }

  /**
   * Main segmentation function
   */
  async function segment(
    imageInfo: ImageRetrivalInfo,
    isFromFunctionModule = false,
  ): Promise<SegmentationResult> {
    // If segmentation is already active, abort it instead of warning
    if (isSegmentationActive.value) {
      abort();
      return { results: [] };
    }
    const currentStudyStore = useCurrentStudyStore();

    // Set segmentation as active
    isSegmentationActive.value = true;

    // Clear any existing queue and results
    clearQueue();
    resultsFromBackend.value = [];

    // Merge default settings with custom settings
    let settings = segmentationSettingsStore.getSettings();
    const studyInfo = currentStudyStore.getStudyInfo;
    const scannerSystem = studyInfo.Manufacturer;

    segmentationMessageLog.value = ['Starting segmentation process...'];
    alert.info(segmentationMessageLog.value.join('\n'));

    try {
      let dicomPullRequestObject: DicomPullRequest[] = [];
      let metaData: MetaData = {
        orientationBySeries: {},
        classificationBySeries: {},
        selectedSeries: [],
      };

      // Get payload based on image info
      if (imageInfo.include && imageInfo.include.length > 0) {
        ({ dicomPullRequestObject, metaData } = await getPayloadFromImageUIDs(
          imageInfo.include,
        ));
      }
      else {
        ({ dicomPullRequestObject, metaData }
          = await getAllImageUIDs(imageInfo));
      }

      // Set total images count
      totalImages.value = dicomPullRequestObject.length;
      processedCount.value = 0;

      // Process payload to include image data if needed
      const processedDicomRequest = dicomPullRequestObject;
      // if (settings.SEND_IMAGES_DIRECTLY) {
      //   processedDicomRequest = await getAllAsImages(dicomPullRequestObject);
      //   metaData.imagesAreAttached = true;
      //   metaData.imagesAreCompressed = settings.IMAGES_COMPRESSED;
      //   metaData.imagesAreEncrypted = settings.ENCRYPT_IMAGES;
      // }

      metaData.postProcessing = settings.POST_PROCESSING;
      if (isFromFunctionModule) {
        metaData = { ...metaData, batchImagesTogether: true };
      }

      settings = updateAutoModelSelection(settings, scannerSystem);

      // Split payload into batches of MAX_IMAGES_PER_LOAD
      const batches: DicomPullRequest[][] = [];
      for (
        let i = 0;
        i < processedDicomRequest.length;
        i += MAX_IMAGES_PER_LOAD
      ) {
        batches.push(processedDicomRequest.slice(i, i + MAX_IMAGES_PER_LOAD));
      }

      totalBatches.value = batches.length;
      currentBatch.value = 0;

      // Create queue items for each batch
      for (let i = 0; i < batches.length; i++) {
        processingQueue.value.push({
          batchIndex: i + 1,
          totalBatches: batches.length,
          payload: batches[i],
          metaData: { ...metaData },
          settings,
        });
      }

      segmentationMessageLog.value.push(
        `Created ${batches.length} batches for processing. Using model: ${settings.SAX_MODEL_NAME}.`,
      );
      alert.info(segmentationMessageLog.value.join('\n'));

      // Start processing the queue
      const results = await processNextBatch();

      // Return the segmentation results
      return { results };
    }
    catch (error) {
      const errorMessage
        = error instanceof Error ? error.message : 'Unknown error occurred';
      segmentationMessageLog.value.push(`Error: ${errorMessage}`);
      alert.error(segmentationMessageLog.value.join('\n'));
      throw error;
    }
    finally {
      // Reset segmentation active flag
      isSegmentationActive.value = false;
    }
  }

  function getSystoleDiastolePhases(
    results: BatchResult[],
    seriesIndex: number,
    targetSlices: number[],
    targetLabel: ContourLabel,
  ): string[] {
    const currentStudyStore = useCurrentStudyStore();
    const endoAreas: Record<string, number> = {};

    const allImageUIDs = results.map(result => result.imageUID);

    if (targetSlices.length === 0) {
      // User is segmenting on column base (all images at phase)
      return allImageUIDs;
    }

    const middleIndex = Math.floor(targetSlices.length / 2);
    const targetSliceIndex = targetSlices[middleIndex];

    const middleSliceImageUIDs = currentStudyStore.getSliceImageUIDs(
      seriesIndex,
      targetSliceIndex,
    );

    if (!middleSliceImageUIDs) {
      return allImageUIDs;
    }

    results.forEach((result) => {
      const { contours, imageUID } = result;

      for (const contour of contours) {
        const { label, points } = contour;
        if (label !== targetLabel || !points || points.length === 0) continue;
        const endoArea = getPolygonAreaArray(points);
        endoAreas[imageUID] = endoArea;
      }
    });

    let minAreaPhaseIndex = 0;
    let minArea = Infinity;
    let maxAreaPhaseIndex = 0;
    let maxArea = 0;

    for (let p = 0; p < middleSliceImageUIDs.length; p++) {
      const imageUID = middleSliceImageUIDs[p];
      const endoArea = endoAreas[imageUID];
      if (endoArea) {
        if (endoArea < minArea) {
          minArea = endoArea;
          minAreaPhaseIndex = p;
        }
        if (endoArea > maxArea) {
          maxArea = endoArea;
          maxAreaPhaseIndex = p;
        }
      }
    }

    const diastolicPhaseUIDs = currentStudyStore.getPhaseImageUIDs(
      seriesIndex,
      maxAreaPhaseIndex,
    );
    const systolicPhaseUIDs = currentStudyStore.getPhaseImageUIDs(
      seriesIndex,
      minAreaPhaseIndex,
    );

    if (!diastolicPhaseUIDs || !systolicPhaseUIDs) {
      return allImageUIDs;
    }

    const allDiaSystolicUIDs = new Set([
      ...diastolicPhaseUIDs,
      ...systolicPhaseUIDs,
    ]);

    // Make sure to only include uids that exist in allImageUIDs (results):
    const filteredUIDs = [...allDiaSystolicUIDs].filter(uid =>
      allImageUIDs.includes(uid),
    );

    // Get all the image UIDs from the diastolic and systolic phases combined:
    return filteredUIDs;
  }

  async function segmentandAdd(imageInfo: ImageRetrivalInfo) {
    const segmentationResult = await segment(imageInfo);
    addContourBatch(segmentationResult.results);
  }

  async function segmentFunctionModule(
    imageInfo: ImageRetrivalInfo,
    seriesIndex: number,
    targetSlices: number[],
    targetLabel: ContourLabel,
  ) {
    const settings = segmentationSettingsStore.getSettings();
    const segmentationResult = await segment(imageInfo, true);
    if (settings.FUNCTION_MODULE_BEHAVIOR === 'auto') {
      const include = getSystoleDiastolePhases(
        segmentationResult.results,
        seriesIndex,
        targetSlices,
        targetLabel,
      );
      addContourBatch(segmentationResult.results, include);
    }
    else if (settings.FUNCTION_MODULE_BEHAVIOR === 'all') {
      // If 'all', add all results regardless of phase
      addContourBatch(segmentationResult.results);
    }
  }

  return {
    segment,
    segmentJobs,
    segmentActiveViewers,
    abort,
    addContourBatch,
    segmentandAdd,
    segmentFunctionModule,
    isActive: isSegmentationActive,
    progress: progressState.value,
  };
}
